import { useState } from 'react';
import { useCatalogos } from '@/components/candidatos/catalogos';
import {
  NIVEL_IDIOMA_LABEL,
  TERNARIO_LABEL,
  TIPO_DOCUMENTO_IDENTIDAD_LABEL,
  type Empleado,
  type NivelIdioma,
  type Ternario,
  type TipoDocumentoIdentidad,
} from '@/lib/personal';

/**
 * Alta y edición de la ficha.
 *
 * Los puestos y las zonas salen del mismo catálogo que la bolsa de empleo
 * (`settings/puestos` y `settings/zones`), así que contratar a un candidato y
 * darlo de alta como empleado hablan el mismo idioma.
 *
 * Los campos sensibles se pueden escribir aquí pero NO se rellenan con lo que
 * ya hubiera guardado: el servidor no los devuelve en la ficha. Se dejan en
 * blanco con el aviso de que hay algo puesto, y solo se mandan si se teclea
 * algo, para que editar el teléfono no borre el IBAN.
 */
interface Props {
  empleado?: Empleado;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}

const TERNARIOS = Object.keys(TERNARIO_LABEL) as Ternario[];
const TIPOS_ID = Object.keys(TIPO_DOCUMENTO_IDENTIDAD_LABEL) as TipoDocumentoIdentidad[];
const NIVELES = Object.keys(NIVEL_IDIOMA_LABEL) as NivelIdioma[];

/** Los que de verdad se hablan por aquí; el resto se escribe a mano. */
const IDIOMAS_HABITUALES = [
  { codigo: 'es', nombre: 'Español' },
  { codigo: 'en', nombre: 'Inglés' },
  { codigo: 'fr', nombre: 'Francés' },
  { codigo: 'de', nombre: 'Alemán' },
  { codigo: 'ar', nombre: 'Árabe' },
  { codigo: 'ro', nombre: 'Rumano' },
];

export default function EmpleadoForm({ empleado, onGuardar, onCancelar }: Props) {
  const { catalogos } = useCatalogos();
  const [f, setF] = useState({
    nombre: empleado?.nombre || '',
    apellidos: empleado?.apellidos || '',
    tipoDocumento: (empleado?.documentoIdentidad?.tipo || 'dni') as TipoDocumentoIdentidad,
    numeroDocumento: '',
    caducaDocumento: empleado?.documentoIdentidad?.caduca || '',
    fechaNacimiento: empleado?.fechaNacimiento || '',
    nacionalidad: empleado?.nacionalidad || '',
    numeroSeguridadSocial: '',
    telefono: empleado?.telefono || '',
    telefonoSecundario: empleado?.telefonoSecundario || '',
    email: empleado?.email || '',
    calle: empleado?.direccion?.calle || '',
    localidad: empleado?.direccion?.localidad || '',
    provincia: empleado?.direccion?.provincia || '',
    cp: empleado?.direccion?.cp || '',
    emergenciaNombre: empleado?.contactoEmergencia?.nombre || '',
    emergenciaTelefono: empleado?.contactoEmergencia?.telefono || '',
    emergenciaRelacion: empleado?.contactoEmergencia?.relacion || '',
    carnetConducir: (empleado?.carnetConducir || 'desconocido') as Ternario,
    vehiculoPropio: (empleado?.vehiculoPropio || 'desconocido') as Ternario,
    puesto: empleado?.puesto || '',
    zona: empleado?.zona || '',
    fechaAlta: empleado?.fechaAlta || '',
    diasVacacionesAnuales: empleado?.diasVacacionesAnuales ? String(empleado.diasVacacionesAnuales) : '',
    iban: '',
    notas: empleado?.notas || '',
  });
  const [idiomas, setIdiomas] = useState(empleado?.idiomas || []);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof f, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  const alternarIdioma = (codigo: string) => {
    setIdiomas((prev) =>
      prev.some((i) => i.codigo === codigo)
        ? prev.filter((i) => i.codigo !== codigo)
        : [...prev, { codigo, nivel: 'medio' as NivelIdioma }],
    );
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      const datos: Record<string, unknown> = {
        nombre: f.nombre.trim(),
        apellidos: f.apellidos.trim() || undefined,
        fechaNacimiento: f.fechaNacimiento || undefined,
        nacionalidad: f.nacionalidad.trim() || undefined,
        telefono: f.telefono.trim() || undefined,
        telefonoSecundario: f.telefonoSecundario.trim() || undefined,
        email: f.email.trim() || undefined,
        direccion: { calle: f.calle.trim(), localidad: f.localidad.trim(), provincia: f.provincia.trim(), cp: f.cp.trim() },
        contactoEmergencia: {
          nombre: f.emergenciaNombre.trim(),
          telefono: f.emergenciaTelefono.trim(),
          relacion: f.emergenciaRelacion.trim(),
        },
        idiomas,
        carnetConducir: f.carnetConducir,
        vehiculoPropio: f.vehiculoPropio,
        puesto: f.puesto || undefined,
        zona: f.zona || undefined,
        fechaAlta: f.fechaAlta || undefined,
        diasVacacionesAnuales: f.diasVacacionesAnuales ? Number(f.diasVacacionesAnuales) : undefined,
        notas: f.notas.trim() || undefined,
      };

      // Los sensibles solo viajan si se han escrito: mandarlos vacíos borraría
      // lo que ya hubiera guardado, que aquí no se muestra.
      if (f.numeroDocumento.trim()) {
        datos.documentoIdentidad = {
          tipo: f.tipoDocumento,
          numero: f.numeroDocumento.trim(),
          caduca: f.caducaDocumento || undefined,
        };
      } else if (empleado?.documentoIdentidad && f.caducaDocumento !== (empleado.documentoIdentidad.caduca || '')) {
        // Cambiar solo la caducidad sin reescribir el número no se puede: el
        // documento se guarda entero. Se avisa en vez de perderlo en silencio.
        throw new Error('Para cambiar la fecha de caducidad hay que volver a escribir el número del documento.');
      }
      if (f.numeroSeguridadSocial.trim()) datos.numeroSeguridadSocial = f.numeroSeguridadSocial.trim();
      if (f.iban.trim()) datos.iban = f.iban.trim();

      await onGuardar(datos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-5">
      <Seccion titulo="Identidad">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Campo etiqueta="Nombre *">
            <input required className="input" value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
          </Campo>
          <Campo etiqueta="Apellidos">
            <input className="input" value={f.apellidos} onChange={(e) => set('apellidos', e.target.value)} />
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo etiqueta="Documento">
            <select className="input" value={f.tipoDocumento} onChange={(e) => set('tipoDocumento', e.target.value)}>
              {TIPOS_ID.map((t) => (
                <option key={t} value={t}>{TIPO_DOCUMENTO_IDENTIDAD_LABEL[t]}</option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Número" pista={empleado?.documentoIdentidad ? 'Hay uno guardado' : 'Dato sensible'}>
            <input
              className="input" placeholder={empleado?.documentoIdentidad ? '•••••••• (sin cambios)' : ''}
              value={f.numeroDocumento} onChange={(e) => set('numeroDocumento', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Caduca el">
            <input type="date" className="input" value={f.caducaDocumento} onChange={(e) => set('caducaDocumento', e.target.value)} />
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo etiqueta="Fecha de nacimiento">
            <input type="date" className="input" value={f.fechaNacimiento} onChange={(e) => set('fechaNacimiento', e.target.value)} />
          </Campo>
          <Campo etiqueta="Nacionalidad">
            <input className="input" value={f.nacionalidad} onChange={(e) => set('nacionalidad', e.target.value)} />
          </Campo>
          <Campo etiqueta="Nº Seguridad Social" pista={empleado?.tieneNumeroSeguridadSocial ? 'Hay uno guardado' : '12 dígitos'}>
            <input
              className="input" placeholder={empleado?.tieneNumeroSeguridadSocial ? '•••••••• (sin cambios)' : ''}
              value={f.numeroSeguridadSocial} onChange={(e) => set('numeroSeguridadSocial', e.target.value)}
            />
          </Campo>
        </div>
      </Seccion>

      <Seccion titulo="Contacto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo etiqueta="Teléfono">
            <input className="input" value={f.telefono} onChange={(e) => set('telefono', e.target.value)} />
          </Campo>
          <Campo etiqueta="Otro teléfono">
            <input className="input" value={f.telefonoSecundario} onChange={(e) => set('telefonoSecundario', e.target.value)} />
          </Campo>
          <Campo etiqueta="Email">
            <input type="email" className="input" value={f.email} onChange={(e) => set('email', e.target.value)} />
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Campo etiqueta="Calle">
              <input className="input" value={f.calle} onChange={(e) => set('calle', e.target.value)} />
            </Campo>
          </div>
          <Campo etiqueta="Localidad">
            <input className="input" value={f.localidad} onChange={(e) => set('localidad', e.target.value)} />
          </Campo>
          <Campo etiqueta="CP">
            <input className="input" value={f.cp} onChange={(e) => set('cp', e.target.value)} />
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo etiqueta="Contacto de emergencia">
            <input className="input" value={f.emergenciaNombre} onChange={(e) => set('emergenciaNombre', e.target.value)} />
          </Campo>
          <Campo etiqueta="Su teléfono">
            <input className="input" value={f.emergenciaTelefono} onChange={(e) => set('emergenciaTelefono', e.target.value)} />
          </Campo>
          <Campo etiqueta="Parentesco">
            <input className="input" placeholder="Hija, marido…" value={f.emergenciaRelacion} onChange={(e) => set('emergenciaRelacion', e.target.value)} />
          </Campo>
        </div>
      </Seccion>

      <Seccion titulo="Puesto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Campo etiqueta="Puesto">
            <select className="input" value={f.puesto} onChange={(e) => set('puesto', e.target.value)}>
              <option value="">— Sin puesto —</option>
              {catalogos.puestos.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Zona">
            <select className="input" value={f.zona} onChange={(e) => set('zona', e.target.value)}>
              <option value="">— Sin zona —</option>
              {catalogos.zonas.map((z) => (
                <option key={z.id} value={z.id}>{z.label}</option>
              ))}
            </select>
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo etiqueta="Fecha de alta">
            <input type="date" className="input" value={f.fechaAlta} onChange={(e) => set('fechaAlta', e.target.value)} />
          </Campo>
          <Campo etiqueta="Vacaciones al año" pista="Días naturales; 30 por defecto">
            <input
              type="number" min={0} max={60} className="input" placeholder="30"
              value={f.diasVacacionesAnuales} onChange={(e) => set('diasVacacionesAnuales', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="IBAN" pista={empleado?.tieneIban ? 'Hay uno guardado' : 'Dato sensible'}>
            <input
              className="input" placeholder={empleado?.tieneIban ? '•••••••• (sin cambios)' : 'ES…'}
              value={f.iban} onChange={(e) => set('iban', e.target.value)}
            />
          </Campo>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Campo etiqueta="Carnet de conducir">
            <select className="input" value={f.carnetConducir} onChange={(e) => set('carnetConducir', e.target.value)}>
              {TERNARIOS.map((t) => <option key={t} value={t}>{TERNARIO_LABEL[t]}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Vehículo propio">
            <select className="input" value={f.vehiculoPropio} onChange={(e) => set('vehiculoPropio', e.target.value)}>
              {TERNARIOS.map((t) => <option key={t} value={t}>{TERNARIO_LABEL[t]}</option>)}
            </select>
          </Campo>
        </div>
      </Seccion>

      <Seccion titulo="Idiomas">
        <div className="flex flex-wrap gap-1.5">
          {IDIOMAS_HABITUALES.map((i) => {
            const puesto = idiomas.find((x) => x.codigo === i.codigo);
            return (
              <button
                key={i.codigo}
                type="button"
                onClick={() => alternarIdioma(i.codigo)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                  puesto ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/60 hover:bg-gray-200'
                }`}
              >
                {i.nombre}
              </button>
            );
          })}
        </div>
        {idiomas.length > 0 && (
          <div className="space-y-2 mt-2">
            {idiomas.map((i) => (
              <div key={i.codigo} className="flex items-center gap-2 text-sm">
                <span className="w-20 text-sereno-dark/60">
                  {IDIOMAS_HABITUALES.find((h) => h.codigo === i.codigo)?.nombre || i.codigo}
                </span>
                <select
                  className="input w-auto text-xs py-1"
                  value={i.nivel}
                  onChange={(e) =>
                    setIdiomas((prev) =>
                      prev.map((x) => (x.codigo === i.codigo ? { ...x, nivel: e.target.value as NivelIdioma } : x)),
                    )
                  }
                >
                  {NIVELES.map((n) => <option key={n} value={n}>{NIVEL_IDIOMA_LABEL[n]}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </Seccion>

      <Campo etiqueta="Notas">
        <textarea className="input" rows={3} value={f.notas} onChange={(e) => set('notas', e.target.value)} />
      </Campo>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancelar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
          {guardando ? 'Guardando…' : empleado ? 'Guardar cambios' : 'Crear ficha'}
        </button>
      </div>
    </form>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">{titulo}</h3>
      {children}
    </div>
  );
}

function Campo({ etiqueta, pista, children }: { etiqueta: string; pista?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-sereno-dark/60">{etiqueta}</span>
      <div className="mt-1">{children}</div>
      {pista && <span className="text-[11px] text-sereno-dark/40">{pista}</span>}
    </label>
  );
}
